import { useState } from "react";

import { FacetGroup, FacetPanel, FacetSheet } from "@/components/filters/FacetSheet";
import { FiltersTrigger } from "@/components/filters/FiltersTrigger";
import { PriceRangeFilter } from "@/components/filters/PriceRangeFilter";
import { KindSwitch } from "@/components/inventory/KindSwitch";
import { type BrowseRefinements, kindRefinements } from "@/lib/browse/browseFilter";
import {
  INLINE_FACETS,
  type StorefrontFacet,
  type StorefrontPatch,
} from "@/lib/browse/storefrontFacets";
import { appliedFacetCount } from "@/lib/filters/facet";

/**
 * The storefront's Filters, on the shared toolbar inventory and labels use: a trigger with the
 * applied count, and a sheet with Price and Type inline and a summary row per other facet
 * (Condition, which takes several, then Game, then Set). The screen renders the applied chips beside
 * the sort, so this owns only the trigger and the sheet.
 *
 * The trigger is the compact icon form, because it shares the search row with the view toggle.
 */
export function BrowseFilterControl(
  { facets, onChange, refinements, showPrice, resultCount, onClearAll }: {
    facets: StorefrontFacet[];
    onChange: (patch: StorefrontPatch) => void;
    refinements: BrowseRefinements;
    showPrice: boolean;
    /** How many products the filters leave, for the sheet's action; undefined while the rest of the
     *  table is still loading, when a count would undercount. */
    resultCount: number | undefined;
    onClearAll: () => void;
  },
) {
  const [open, setOpen] = useState(false);
  const count = appliedFacetCount(facets);

  return (
    <>
      <FiltersTrigger
        testID="browse-filter-control"
        count={count}
        compact
        onPress={() => setOpen(true)}
      />
      <FacetSheet
        visible={open}
        onClose={() => setOpen(false)}
        actionLabel={resultCount === undefined
          ? "Show products"
          : `Show ${resultCount.toLocaleString()} ${resultCount === 1 ? "product" : "products"}`}
      >
        <FacetPanel
          testIDPrefix="browse"
          title="Filter products"
          facets={facets.filter((f) => !INLINE_FACETS.has(f.key))}
          onChange={onChange}
          appliedCount={count}
          onClearAll={onClearAll}
          inline={
            <>
              {showPrice
                ? (
                  <PriceRangeFilter
                    testIDPrefix="browse-filter"
                    value={{ min: refinements.minPrice, max: refinements.maxPrice }}
                    onChange={({ min, max }) =>
                      onChange({ refinements: { minPrice: min, maxPrice: max } })}
                  />
                )
                : null}
              <FacetGroup label="Type">
                <KindSwitch
                  anyLabel="All"
                  value={refinements.kind}
                  // Clears what the new Type cannot carry (`kindRefinements`).
                  onChange={(kind) => onChange({ refinements: kindRefinements(kind) })}
                />
              </FacetGroup>
            </>
          }
        />
      </FacetSheet>
    </>
  );
}
